<div class="p-5 max-w-[1600px] mx-auto">
  <!-- Header -->
  <div class="flex justify-between items-center mb-5">
    <h1 class="text-2xl font-medium">{{ 'waters.list.title' | translate }}</h1>
    <button mat-flat-button (click)="navigateToCreate()">
      <mat-icon>add</mat-icon>
      {{ 'waters.list.create' | translate }}
    </button>
  </div>

  <!-- Search + column picker -->
  <div class="flex gap-3 mb-4 items-center">
    <mat-form-field class="flex-1" subscriptSizing="dynamic">
      <mat-label>{{ 'waters.list.search-placeholder' | translate }}</mat-label>
      <input #searchBox matInput [value]="searchInput()" (input)="onSearchInput(searchBox.value)" />
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <button
      mat-icon-button
      [disabled]="!hasActiveFilters()"
      (click)="clearAllFilters()"
      matTooltip="{{ 'waters.filter.clear-all' | translate }}"
      [attr.aria-label]="'waters.filter.clear-all' | translate">
      <mat-icon>filter_alt_off</mat-icon>
    </button>

    <button
      mat-icon-button
      [matMenuTriggerFor]="columnMenu"
      matTooltip="{{ 'waters.list.column-picker' | translate }}"
      [attr.aria-label]="'waters.list.column-picker' | translate">
      <mat-icon>view_column</mat-icon>
    </button>
  </div>

  <mat-card>
    <mat-card-content class="!p-0">
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="waters()" class="w-full">

          <ng-container matColumnDef="title">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.title' | translate }}</th>
            <td mat-cell *matCellDef="let w" class="font-medium whitespace-nowrap">{{ w.title }}</td>
          </ng-container>

          <ng-container matColumnDef="urlTitle">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.urlTitle' | translate }}</th>
            <td mat-cell *matCellDef="let w" class="text-sm text-gray-500">{{ w.urlTitle }}</td>
          </ng-container>

          <ng-container matColumnDef="isActive">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.isActive' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.isActive" /></td>
          </ng-container>

          <ng-container matColumnDef="country">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.country' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ codeListsService.countryMap().get(w.fkCountryId) }}</td>
          </ng-container>

          <ng-container matColumnDef="exclusive">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.exclusive' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ codeListsService.exclusiveMap().get(w.fkExclusiveId) }}</td>
          </ng-container>

          <ng-container matColumnDef="sites">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.sites' | translate }}</th>
            <td mat-cell *matCellDef="let w" class="text-sm">{{ getSiteNames(w.siteIds) }}</td>
          </ng-container>

          <ng-container matColumnDef="spots">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.spots' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.spots }}</td>
          </ng-container>

          <ng-container matColumnDef="coTravelerSpots">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.coTravelerSpots' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.coTravelerSpots }}</td>
          </ng-container>

          <ng-container matColumnDef="difficulty">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.difficulty' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.difficulty ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="waterSize">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.waterSize' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.waterSize !== null && w.waterSize !== undefined ? w.waterSize + ' ha' : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="carpWeight">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.carpWeight' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.carpWeight !== null && w.carpWeight !== undefined ? w.carpWeight + ' kg' : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="price">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.price' | translate }}</th>
            <td mat-cell *matCellDef="let w" class="whitespace-nowrap">{{ w.price !== null && w.price !== undefined ? (w.price | currency: 'EUR') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="pricePerSpot">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.pricePerSpot' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.pricePerSpot" /></td>
          </ng-container>

          <ng-container matColumnDef="dogs">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.dogs' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.dogs" /></td>
          </ng-container>

          <ng-container matColumnDef="rowingBoat">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.rowingBoat' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.rowingBoat" /></td>
          </ng-container>

          <ng-container matColumnDef="baitBoat">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.baitBoat' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.baitBoat" /></td>
          </ng-container>

          <ng-container matColumnDef="restaurant">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.restaurant' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.restaurant" /></td>
          </ng-container>

          <ng-container matColumnDef="hasAccommodations">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.hasAccommodations' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.hasAccommodations" /></td>
          </ng-container>

          <ng-container matColumnDef="ownTent">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.ownTent' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.ownTent" /></td>
          </ng-container>

          <ng-container matColumnDef="ownCaravanCamper">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.ownCaravanCamper' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.ownCaravanCamper" /></td>
          </ng-container>

          <ng-container matColumnDef="carOnSpot">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.carOnSpot' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.carOnSpot" /></td>
          </ng-container>

          <ng-container matColumnDef="week">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.week' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.week" /></td>
          </ng-container>

          <ng-container matColumnDef="midweek">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.midweek' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.midweek" /></td>
          </ng-container>

          <ng-container matColumnDef="weekend">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.weekend' | translate }}</th>
            <td mat-cell *matCellDef="let w"><app-bool [value]="w.weekend" /></td>
          </ng-container>

          <ng-container matColumnDef="averageRating">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.averageRating' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.averageRating | number: '1.1-1' }}</td>
          </ng-container>

          <ng-container matColumnDef="amountOfRatings">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.amountOfRatings' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.amountOfRatings }}</td>
          </ng-container>

          <ng-container matColumnDef="views">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.views' | translate }}</th>
            <td mat-cell *matCellDef="let w">{{ w.views ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="lastUpdate">
            <th mat-header-cell *matHeaderCellDef>{{ 'waters.field.lastUpdate' | translate }}</th>
            <td mat-cell *matCellDef="let w" class="whitespace-nowrap">{{ w.lastUpdate ? (w.lastUpdate | date: 'mediumDate') : '—' }}</td>
          </ng-container>

          <!-- Actions -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="!w-16"></th>
            <td mat-cell *matCellDef="let w">
              <button
                mat-icon-button
                color="primary"
                (click)="$event.stopPropagation(); navigateToWater(w.id)"
                matTooltip="{{ 'waters.list.edit' | translate }}"
                [attr.aria-label]="'waters.list.edit' | translate">
                <mat-icon>edit</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- Filter header columns -->
          @for (col of allColumns; track col.key) {
            <ng-container [matColumnDef]="'filter-' + col.key">
              <th mat-header-cell *matHeaderCellDef [class.!p-1]="col.filterType !== 'none'">
                @switch (col.filterType) {
                  @case ('text') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <input #filterBox matInput
                        placeholder="{{ 'waters.filter.placeholder' | translate }}"
                        [value]="getTextFilterValue(col.filterField!)"
                        (input)="onTextFilterInput(col.filterField!, filterBox.value)" />
                    </mat-form-field>
                  }
                  @case ('dropdown') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <mat-select
                        [value]="getDropdownFilterValue(col.filterField!)"
                        (selectionChange)="onDropdownFilterChange(col.filterField!, $event.value)">
                        <mat-option value="">{{ 'waters.filter.all' | translate }}</mat-option>
                        @for (opt of dropdownSources[col.dropdownSource!](); track opt.id) {
                          <mat-option [value]="opt.id">{{ opt.name }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('boolean') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <mat-select
                        [value]="getBooleanFilterValue(col.filterField!)"
                        (selectionChange)="onDropdownFilterChange(col.filterField!, $event.value)">
                        <mat-option value="">{{ 'waters.filter.all' | translate }}</mat-option>
                        <mat-option [value]="true">{{ 'waters.filter.yes' | translate }}</mat-option>
                        <mat-option [value]="false">{{ 'waters.filter.no' | translate }}</mat-option>
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('daterange') {
                    <div class="flex gap-1 items-center">
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <mat-label>{{ 'waters.filter.from' | translate }}</mat-label>
                        <input matInput [matDatepicker]="fromPicker"
                          [value]="getDateFilterValue(col.filterFieldFrom!)"
                          (dateChange)="onDateFilterChange(col.filterFieldFrom!, $event.value)" />
                        @if (getDateFilterValue(col.filterFieldFrom!)) {
                          <button matIconSuffix mat-icon-button type="button"
                            (click)="onDateFilterChange(col.filterFieldFrom!, null)"
                            matTooltip="{{ 'waters.filter.clear' | translate }}"
                            [attr.aria-label]="'waters.filter.clear' | translate">
                            <mat-icon>close</mat-icon>
                          </button>
                        } @else {
                          <mat-datepicker-toggle matIconSuffix [for]="fromPicker" />
                        }
                        <mat-datepicker #fromPicker />
                      </mat-form-field>
                      <span class="text-gray-400 text-xs">→</span>
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <mat-label>{{ 'waters.filter.to' | translate }}</mat-label>
                        <input matInput [matDatepicker]="toPicker"
                          [value]="getDateFilterValue(col.filterFieldTo!)"
                          (dateChange)="onDateFilterChange(col.filterFieldTo!, $event.value)" />
                        @if (getDateFilterValue(col.filterFieldTo!)) {
                          <button matIconSuffix mat-icon-button type="button"
                            (click)="onDateFilterChange(col.filterFieldTo!, null)"
                            matTooltip="{{ 'waters.filter.clear' | translate }}"
                            [attr.aria-label]="'waters.filter.clear' | translate">
                            <mat-icon>close</mat-icon>
                          </button>
                        } @else {
                          <mat-datepicker-toggle matIconSuffix [for]="toPicker" />
                        }
                        <mat-datepicker #toPicker />
                      </mat-form-field>
                    </div>
                  }
                  @case ('numrange') {
                    <div class="flex gap-1">
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <input matInput type="number"
                          placeholder="{{ 'waters.filter.min' | translate }}"
                          [value]="getRangeFilterValue(col.filterFieldFrom!)"
                          (change)="onRangeFilterChange(col.filterFieldFrom!, $any($event.target).value, true)" />
                      </mat-form-field>
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <input matInput type="number"
                          placeholder="{{ 'waters.filter.max' | translate }}"
                          [value]="getRangeFilterValue(col.filterFieldTo!)"
                          (change)="onRangeFilterChange(col.filterFieldTo!, $any($event.target).value, true)" />
                      </mat-form-field>
                    </div>
                  }
                }
              </th>
            </ng-container>
          }

          <ng-container matColumnDef="filter-actions">
            <th mat-header-cell *matHeaderCellDef class="!w-16"></th>
          </ng-container>

          <!-- No-data row -->
          <tr *matNoDataRow>
            @if (!watersResource.isLoading()) {
              <td [attr.colspan]="displayedColumns().length" class="p-0">
                <div class="text-center p-10">
                  <mat-icon class="!text-6xl !w-16 !h-16 !text-gray-400 mb-4">water</mat-icon>
                  <p class="text-gray-700 text-base">{{ 'waters.list.no-waters' | translate }}</p>
                </div>
              </td>
            }
          </tr>

          <!-- Row definitions -->
          <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
          <tr mat-header-row *matHeaderRowDef="filterColumns()" class="filter-row"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns()"
            (click)="navigateToWater(row.id)"
            class="cursor-pointer hover:bg-gray-50"></tr>
        </table>
      </div>

      @if (totalCount() > 0) {
        <mat-paginator
          [length]="totalCount()"
          [pageSize]="pageSize()"
          [pageIndex]="page() - 1"
          [pageSizeOptions]="[10, 25, 50, 100]"
          [showFirstLastButtons]="true"
          (page)="onPageChange($event)"
          class="border-t border-gray-200" />
      }
    </mat-card-content>
  </mat-card>
</div>

<!-- Column picker menu -->
<mat-menu #columnMenu="matMenu">
  <div class="px-2 py-1 min-w-48" (click)="$event.stopPropagation()">
    <p class="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 pb-2">
      {{ 'waters.list.column-picker' | translate }}
    </p>
    @for (col of allColumns; track col.key) {
      <div class="flex items-center px-2 py-1">
        <mat-checkbox [checked]="isColumnVisible(col.key)" (change)="toggleColumn(col.key)">
          {{ col.labelKey | translate }}
        </mat-checkbox>
      </div>
    }
  </div>
</mat-menu>
